/**
 * In-app session notices → the notification drawer's item contract.
 *
 * A notice is a pointer, not a state: it says something happened to a session
 * and links to it. The destination always fetches the session's current state
 * (SessionDetailPage), so an old notice can never show an outdated outcome or
 * an action that no longer applies.
 */

const SESSION_PATH_RE = /^\/history\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const SEVERITY_TONE = Object.freeze({ CRITICAL: 'danger', ERROR: 'danger', WARNING: 'warning', INFO: 'info' });

/**
 * The only link a notice may carry: this app's own session-detail route for a
 * well-formed session id. Anything else — another route, an absolute URL, a
 * scheme — is dropped, and the notice is shown without a link.
 */
export function safeNoticeHref(href) {
  return typeof href === 'string' && SESSION_PATH_RE.test(href) ? href : undefined;
}

export function toDrawerItem(notice) {
  if (!notice || typeof notice !== 'object' || notice.id == null || typeof notice.title !== 'string') return null;
  return {
    id: `session-notice-${notice.id}`,
    title: notice.title,
    body: typeof notice.body === 'string' ? notice.body : undefined,
    timestamp: typeof notice.occurredAt === 'string' ? notice.occurredAt : undefined,
    unread: notice.unread === true,
    href: safeNoticeHref(notice.href),
    tone: SEVERITY_TONE[notice.severity] || 'neutral',
    sessionUid: typeof notice.sessionUid === 'string' ? notice.sessionUid : null,
    sessionVersion: Number.isInteger(notice.sessionVersion) ? notice.sessionVersion : null,
  };
}

export function toDrawerItems(notices) {
  return (Array.isArray(notices) ? notices : []).map(toDrawerItem).filter(Boolean);
}
